import React from 'react'
import { api } from '../lib/api'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'
import { formatMenuInstant } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'
import ManagedCustomerAutocomplete from '../components/ManagedCustomerAutocomplete'

function openDaumPostcode(onComplete) {
  if (!window?.daum?.Postcode) {
    throw new Error('daum_postcode_not_loaded')
  }
  new window.daum.Postcode({
    oncomplete: function (data) {
      onComplete && onComplete(data)
    },
  }).open()
}

function pick(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k]
    if (v === null || v === undefined) continue
    const s = String(v).trim()
    if (s) return s
  }
  return null
}

function formatDateDigits(v) {
  if (!v) return null
  const s = String(v).replace(/[^\d]/g, '')
  if (s.length === 8) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
  if (s.length === 6) return `${s.slice(0, 4)}-${s.slice(4, 6)}`
  return String(v)
}

function formatNumber(v) {
  if (v === null || v === undefined) return null
  const n = Number(String(v).replace(/,/g, '').trim())
  if (!Number.isFinite(n)) return String(v)
  return n.toLocaleString('ko-KR')
}

function parseMoneyInput(v) {
  if (v === null || v === undefined) return null
  const s = String(v).replace(/[^\d]/g, '')
  if (!s) return null
  const n = Number(s)
  if (!Number.isFinite(n)) return null
  return Math.max(0, Math.floor(n))
}

function formatMoney(n) {
  if (n === null || n === undefined) return ''
  const v = Number(n)
  if (!Number.isFinite(v)) return ''
  return v.toLocaleString('ko-KR')
}

function numericPart(v) {
  const s = String(v || '').replace(/[^\d]/g, '')
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

function isBasementFloorName(v) {
  const s = String(v || '').trim()
  if (!s) return false
  if (s.includes('지하')) return true
  if (s.startsWith('지')) return true // 예: 지1, 지2
  if (s.toUpperCase().startsWith('B')) return true // 예: B1
  return false
}

function floorOrderName(flrNoNm) {
  const s = String(flrNoNm || '').trim()
  if (!s) return 999999
  const n = numericPart(s)
  if (!Number.isFinite(n)) return 999999
  return isBasementFloorName(s) ? -n : n
}

function compareFloorName(a, b) {
  const oa = floorOrderName(a)
  const ob = floorOrderName(b)
  if (oa !== ob) return oa - ob
  return String(a || '').localeCompare(String(b || ''), 'ko-KR')
}

function compareDongHo(a, b) {
  const na = numericPart(a)
  const nb = numericPart(b)
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb
  return String(a || '').localeCompare(String(b || ''), 'ko-KR')
}

const FIRE_BIZ_TYPES = [
  {
    id: 'HOME_APT',
    label: '주택/아파트',
    endorsements: [
      '도난(빈집, 명기) 가재',
      '임시거처',
      '가전제품고장수리비용',
      '화재폭발배상책임',
      '임대인배상책임(임대사업자)',
    ],
  },
  {
    id: 'FOOD',
    label: '일반·휴게 음식점',
    endorsements: [
      '음식물배상책임',
      '가스사고배상책임',
      '시설소유자배상책임',
      '구내 배상(화상)손해',
      '구내폭발발열손해',
      '화재폭발배상책임',
      '점포휴업손해',
    ],
  },
  {
    id: 'FACTORY',
    label: '공장',
    endorsements: [
      '전기손해',
      '구내폭발발열손해',
      '화재폭발배상책임',
    ],
  },
  {
    id: 'SMALL_RETAIL',
    label: '소형판매시설',
    endorsements: [
      '시설소유자배상책임',
      '점포휴업손해',
      '유리손해',
      '도난손해',
      '구내폭발발열손해',
      '화재폭발배상책임',
    ],
  },
  {
    id: 'BEAUTY',
    label: '미용실',
    endorsements: [
      '이미용사 배상책임',
      '시설소유자배상책임',
      '화재폭발배상책임',
      '점포휴업손해',
    ],
  },
  {
    id: 'ACADEMY',
    label: '학원',
    endorsements: [
      '학원및교습소배상책임',
      '학원및교습소 치료비추가',
      '화재폭발배상책임',
      '점포휴업손해',
    ],
  },
  {
    id: 'SPORTS',
    label: '오락 및 체육시설',
    endorsements: [
      '전기손해',
      '시설소유자배상책임',
      '구내폭발발열손해',
      '화재폭발배상책임',
    ],
  },
  {
    id: 'CAR_SERVICE',
    label: '차량정비업체',
    endorsements: [
      '차량정비업자배상책임',
      '구내폭발발열손해',
      '화재폭발배상책임',
      '점포휴업손해',
    ],
  },
  {
    id: 'OTHER',
    label: '기타',
    endorsements: [],
  },
]

const FIRE_ENDORSEMENTS_ALL = (() => {
  const out = []
  const seen = new Set()
  for (const t of FIRE_BIZ_TYPES) {
    for (const e of (t.endorsements || [])) {
      const k = String(e || '').trim()
      if (!k || seen.has(k)) continue
      seen.add(k)
      out.push(k)
    }
  }
  return out
})()

function recommendBizTypesFromPurposeText(purposeText) {
  const s = String(purposeText || '').trim()
  if (!s) return []
  const t = s.toLowerCase()

  // 건축물대장 용도는 업종을 완전히 특정하기 어려워 "후보 추천" 형태로 복수 반환한다.
  const out = []
  const add = (id) => {
    if (!id) return
    if (id === 'OTHER') return
    if (!out.includes(id)) out.push(id)
  }

  // 주거
  if (t.includes('아파트') || t.includes('공동주택') || t.includes('연립') || t.includes('다세대') || t.includes('주택')) {
    add('HOME_APT')
  }

  // 공장/제조
  if (t.includes('공장') || t.includes('제조') || t.includes('생산')) {
    add('FACTORY')
  }

  // 음식점 계열
  if (
    t.includes('음식') || t.includes('휴게') || t.includes('일반음식') || t.includes('식당')
    || t.includes('카페') || t.includes('제과') || t.includes('베이커리') || t.includes('주점')
  ) {
    add('FOOD')
  }

  // 판매/소매
  if (
    t.includes('판매') || t.includes('소매') || t.includes('도소매') || t.includes('마트')
    || t.includes('상점') || t.includes('편의') || t.includes('쇼핑')
  ) {
    add('SMALL_RETAIL')
  }

  // 체육/오락/운동
  if (
    t.includes('체육') || t.includes('오락') || t.includes('운동') || t.includes('골프')
    || t.includes('헬스') || t.includes('수영') || t.includes('당구') || t.includes('노래') || t.includes('pc')
  ) {
    add('SPORTS')
  }

  // 자동차 정비(건축물대장 용도에 직접 나오진 않지만 텍스트가 있을 때만 추천)
  if (t.includes('자동차') || t.includes('차량') || t.includes('정비') || t.includes('카센터')) {
    add('CAR_SERVICE')
  }

  // 업종이 텍스트로 명시되는 경우(희박하지만), 추천만 제공
  if (t.includes('미용') || t.includes('이용') || t.includes('네일') || t.includes('피부')) add('BEAUTY')
  if (t.includes('학원') || t.includes('교습') || t.includes('교육')) add('ACADEMY')

  // 근린생활시설은 매우 광범위 → 후보군만 제시
  if (t.includes('근린생활') || t.includes('근생')) {
    add('SMALL_RETAIL')
    add('FOOD')
    add('BEAUTY')
    add('ACADEMY')
  }

  return out
}

function mergeEndorsements(...groups) {
  const out = []
  const seen = new Set()
  for (const group of groups) {
    for (const item of (Array.isArray(group) ? group : [])) {
      const value = String(item || '').trim()
      if (!value || seen.has(value)) continue
      seen.add(value)
      out.push(value)
    }
  }
  return out
}

function extractKoreanRegion(...addresses) {
  const text = addresses.map((v) => String(v || '').trim()).find(Boolean) || ''
  if (!text) return { sido: '', sigungu: '' }
  const tokens = text.split(/\s+/).filter(Boolean)
  const sido = tokens[0] || ''
  const sigungu = tokens.find((token, idx) => idx > 0 && /시$|군$|구$/.test(token)) || ''
  return { sido, sigungu }
}

function statLabel(row, keys) {
  const parts = keys
    .map((key) => String(row?.[key] || '').trim())
    .filter(Boolean)
  return parts.join(' ')
}

function buildFireOccurrenceRecommendation(stats, selectedBizType) {
  if (!stats || stats.status !== 'READY') {
    return { endorsements: [], reasons: [], overview: null }
  }
  const topCauses = Array.isArray(stats.topCauses) ? stats.topCauses : []
  const topPlaces = Array.isArray(stats.topPlaces) ? stats.topPlaces : []
  const text = [...topCauses, ...topPlaces]
    .map((row) => Object.values(row || {}).join(' '))
    .join(' ')
    .toLowerCase()

  const add = []
  const reason = []
  const push = (endorsement, why) => {
    if (!FIRE_ENDORSEMENTS_ALL.includes(endorsement)) return
    add.push(endorsement)
    if (why) reason.push(why)
  }

  if (/전기|누전|합선|단락/.test(text)) {
    push('전기손해', '지역 통계에서 전기적 요인이 확인됩니다.')
  }
  if (/가스|폭발|발열/.test(text)) {
    push('구내폭발발열손해', '폭발·발열 또는 가스 관련 위험을 함께 확인합니다.')
    push('화재폭발배상책임', '화재·폭발 배상책임 검토가 필요합니다.')
  }
  if (/음식|조리|주방|식당|카페/.test(text) || selectedBizType?.id === 'FOOD') {
    push('음식물배상책임', '음식점 업종은 음식물배상책임을 우선 확인합니다.')
    push('가스사고배상책임', '음식점 업종은 가스사고배상책임을 함께 확인합니다.')
  }
  if (/상가|판매|점포|근린|소매/.test(text) || selectedBizType?.id === 'SMALL_RETAIL') {
    push('점포휴업손해', '점포 운영 중단 손해를 함께 검토합니다.')
    push('시설소유자배상책임', '시설 이용자 사고 가능성을 함께 검토합니다.')
  }
  if (/공장|제조|기계/.test(text) || selectedBizType?.id === 'FACTORY') {
    push('구내폭발발열손해', '공장·제조 용도는 폭발·발열 위험을 함께 확인합니다.')
    push('전기손해', '공장·제조 용도는 전기손해를 함께 확인합니다.')
  }

  return {
    endorsements: mergeEndorsements(add),
    reasons: mergeEndorsements(reason).slice(0, 3),
    overview: stats.overview || null,
  }
}

function Modal({ open, title, onClose, headerRight, children }) {
  React.useEffect(() => {
    if (!open) return
    function onEsc(e) {
      if (e.key === 'Escape') onClose && onClose()
    }
    window.addEventListener('keydown', onEsc)
    return () => window.removeEventListener('keydown', onEsc)
  }, [open, onClose])

  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/40 p-4 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose && onClose()
      }}
    >
      <div className="w-full max-w-5xl rounded-xl bg-white shadow-xl border">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-slate-900">{title || '상세'}</div>
          <div className="flex items-center gap-2">
            {headerRight || null}
            <button
              type="button"
              className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
              onClick={() => onClose && onClose()}
            >
              닫기
            </button>
          </div>
        </div>
        <div className="p-4 max-h-[76vh] overflow-auto">
          {children}
        </div>
      </div>
    </div>
  )
}

function BuildingCompactTable({ title, obj }) {
  const items = React.useMemo(() => {
    const defs = [
      { label: '건물명', keys: ['bldNm'], span: 3 },
      { label: '주용도', keys: ['mainPurpsCdNm', 'mainPurpsCd'] },
      { label: '구조', keys: ['strctCdNm', 'strctCd'] },
      { label: '사용승인(준공)', keys: ['useAprDay', 'pmsDay', 'useAprDt'], format: formatDateDigits },
      { label: '연면적', keys: ['totArea'], format: (v) => `${formatNumber(v)} ㎡` },
      { label: '건축면적', keys: ['archArea'], format: (v) => `${formatNumber(v)} ㎡` },
      { label: '건폐율', keys: ['bcRat'], format: (v) => `${v}%` },
      { label: '용적률', keys: ['vlRat'], format: (v) => `${v}%` },
      { label: '지상층수', keys: ['grndFlrCnt', 'grndFlrCo', 'grndFlr'] },
      { label: '지하층수', keys: ['ugrndFlrCnt', 'ugrndFlrCo', 'ugrndFlr'] },
      { label: '세대수', keys: ['hhldCnt', 'hhldCo'] },
      { label: '가구수', keys: ['fmlyCnt', 'fmlyCo'] },
      { label: '내진설계', keys: ['rserthqkDsgnApplyYn'] },
      { label: '높이', keys: ['heit'], format: (v) => `${formatNumber(v)} m` },
      // 대지위치는 총괄개요에만 표시(표제부에는 중복이라 숨김)
      ...(title === '총괄개요' ? [
        { label: '대지위치(지번)', keys: ['platPlc', 'platPlcNm'], span: 3 },
        { label: '대지위치(도로명)', keys: ['newPlatPlc', 'newPlatPlcNm'], span: 3 },
      ] : []),
    ]

    const out = []
    for (const d of defs) {
      const raw = pick(obj, d.keys)
      if (!raw) continue
      const value = d.format ? d.format(raw) : raw
      if (!value) continue
      out.push({ label: d.label, value, span: d.span || 1 })
    }
    return out
  }, [obj])

  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">{title}</div>
      {items.length === 0 ? (
        <div className="px-3 py-3 text-sm text-slate-500">표시할 상세 항목이 없습니다.</div>
      ) : (
        <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          {items.map((it) => (
            <div
              key={it.label}
              className={[
                'rounded border bg-slate-50 px-3 py-2',
                it.span === 3 ? 'md:col-span-3' : it.span === 2 ? 'md:col-span-2' : '',
              ].join(' ').trim()}
            >
              <div className="text-[11px] font-semibold text-slate-500">{it.label}</div>
              <div className="mt-0.5 text-sm text-slate-900 break-words">{it.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AmountTable({ amounts }) {
  const a = amounts || {}
  const rows = [
    { label: '건물', value: a.building },
    { label: '가재', value: a.household },
    { label: '집기', value: a.contents },
    { label: '시설', value: a.facility },
    { label: '동산', value: a.movables },
    { label: '기계', value: a.machinery },
  ]
  const total = rows.reduce((s, r) => s + (typeof r.value === 'number' ? r.value : 0), 0)
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">보험가액(입력, 만원)</div>
      <div className="p-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {rows.map((r) => (
            <div key={r.label} className="rounded border bg-slate-50 px-3 py-2 text-sm">
              <div className="text-[11px] font-semibold text-slate-500">{r.label}</div>
              <div className="mt-0.5 font-medium text-slate-900 break-words">
                {typeof r.value === 'number' ? `${formatMoney(r.value)} 만원` : '-'}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 rounded border bg-white px-3 py-2 text-sm flex items-center justify-between gap-3">
          <div className="text-slate-700 font-semibold">합계</div>
          <div className="text-slate-900 font-semibold break-words">
            {total > 0 ? `${formatMoney(total)} 만원` : '-'}
          </div>
        </div>
      </div>
    </div>
  )
}

function UnitAreaTable({ dongNm, hoNm, rows }) {
  const d = String(dongNm || '').trim()
  const h = String(hoNm || '').trim()
  const list = Array.isArray(rows) ? rows : []
  const sums = React.useMemo(() => {
    const out = { 전유: 0, 공용: 0 }
    for (const r of list) {
      const gb = String(r?.exposPubuseGbCdNm || '').trim()
      const area = Number(String(r?.area || '').replace(/,/g, '').trim())
      if (!Number.isFinite(area)) continue
      if (gb.includes('전유')) out.전유 += area
      else out.공용 += area
    }
    return out
  }, [list])
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">전유부(선택)</div>
      {!d || !h ? (
        <div className="px-3 py-3 text-sm text-slate-600 bg-slate-50">
          동/호를 선택 후 <span className="font-semibold text-slate-900">동/호 적용</span>을 누르면 전유/공용면적(전유부) 상세가 표시됩니다.
        </div>
      ) : (
        <>
          <div className="px-3 py-2 text-sm text-slate-700 border-b bg-slate-50 flex flex-wrap items-center justify-between gap-2">
            <div>동/호: <span className="font-semibold text-slate-900">{d} / {h}</span></div>
            <div className="text-xs text-slate-600">
              전유 합계: <span className="font-semibold text-slate-900">{sums.전유 ? sums.전유.toFixed(3) : '-'}</span> ㎡
              <span className="mx-2">|</span>
              공용 합계: <span className="font-semibold text-slate-900">{sums.공용 ? sums.공용.toFixed(3) : '-'}</span> ㎡
            </div>
          </div>
          {list.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-500">전유/공용면적 정보가 없습니다.</div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-white text-slate-600">
                  <tr className="border-b">
                    <th className="text-left px-3 py-2 whitespace-nowrap">구분</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap">층</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap">층구분</th>
                    <th className="text-left px-3 py-2">용도</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap">구조</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">면적(㎡)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {list.map((r, idx) => (
                    <tr key={`${r?.exposPubuseGbCdNm || 'row'}-${idx}`} className="text-slate-900">
                      <td className="px-3 py-2 whitespace-nowrap">{r?.exposPubuseGbCdNm || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r?.flrNoNm || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r?.flrGbCdNm || '-'}</td>
                      <td className="px-3 py-2">{r?.purpsNm || r?.mainPurpsCdNm || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r?.strctCdNm || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">{r?.area ? formatNumber(r.area) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function FloorTable({ rows, selectedMode, selectedFloorNoNm, onSelectMode, onSelectFloor }) {
  const mode = selectedMode || 'ALL'
  const selected = String(selectedFloorNoNm || '').trim()
  const [open, setOpen] = React.useState(false)
  const [detailOpen, setDetailOpen] = React.useState(false)

  function floorOrder(flrNoNm) {
    return floorOrderName(flrNoNm)
  }

  const sorted = React.useMemo(() => {
    const list = Array.isArray(rows) ? rows : []
    const copy = [...list]
    copy.sort((a, b) => {
      const da = String(a?.dongNm || '').trim()
      const db = String(b?.dongNm || '').trim()
      if (da || db) {
        const c = compareDongHo(da, db)
        if (c !== 0) return c
      }
      const oa = floorOrder(a?.flrNoNm)
      const ob = floorOrder(b?.flrNoNm)
      if (oa !== ob) return oa - ob
      return String(a?.flrNoNm || '').localeCompare(String(b?.flrNoNm || ''), 'ko-KR')
    })
    return copy
  }, [rows])

  const floorOptions = React.useMemo(() => {
    const set = new Set()
    for (const r of sorted) {
      const v = String(r?.flrNoNm || '').trim()
      if (v) set.add(v)
    }
    const arr = Array.from(set)
    arr.sort(compareFloorName)
    return arr
  }, [sorted])

  const filtered = React.useMemo(() => {
    if (mode === 'ONE' && selected) {
      return sorted.filter((r) => String(r?.flrNoNm || '').trim() === selected)
    }
    return sorted
  }, [sorted, mode, selected])

  const floorSummary = React.useMemo(() => {
    // 선택된 층(또는 전체)에 대해 용도/구조/층구분 기준으로 면적을 합산해서 요약
    const map = new Map()
    for (const r of filtered) {
      const key = [
        String(r?.dongNm || '').trim(),
        String(r?.flrNoNm || '').trim(),
        String(r?.flrGbCdNm || '').trim(),
        String(r?.mainPurpsCdNm || '').trim(),
        String(r?.strctCdNm || '').trim(),
        String(r?.mainAtchGbCdNm || '').trim(),
        String(r?.areaExctYn || '').trim(),
      ].join('|')
      const area = Number(String(r?.area || '').replace(/,/g, '').trim())
      const prev = map.get(key) || { count: 0, areaSum: 0, r }
      prev.count += 1
      if (Number.isFinite(area)) prev.areaSum += area
      map.set(key, prev)
    }
    const arr = Array.from(map.values()).map((v) => ({
      dongNm: v.r?.dongNm,
      flrNoNm: v.r?.flrNoNm,
      flrGbCdNm: v.r?.flrGbCdNm,
      mainPurpsCdNm: v.r?.mainPurpsCdNm,
      strctCdNm: v.r?.strctCdNm,
      mainAtchGbCdNm: v.r?.mainAtchGbCdNm,
      areaExctYn: v.r?.areaExctYn,
      count: v.count,
      areaSum: v.areaSum,
    }))
    arr.sort((a, b) => {
      const c = compareDongHo(String(a?.dongNm || '').trim(), String(b?.dongNm || '').trim())
      if (c !== 0) return c
      return floorOrder(a.flrNoNm) - floorOrder(b.flrNoNm)
    })
    return arr
  }, [filtered])

  const view = React.useMemo(() => {
    if (mode === 'ONE' && selected) return filtered
    if (open) return filtered
    return filtered.slice(0, 30)
  }, [filtered, mode, selected, open])

  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900 flex items-center justify-between gap-2">
        <span>층별개요</span>
        {mode !== 'ONE' && filtered.length > 30 && (
          <button
            type="button"
            className="text-xs rounded border px-2 py-1 hover:bg-slate-50"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? '접기' : `펼치기(${filtered.length}건)`}
          </button>
        )}
      </div>

      <div className="px-3 py-2 border-b bg-slate-50">
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="floorModeInTable"
              checked={mode === 'ALL'}
              onChange={() => {
                onSelectMode && onSelectMode('ALL')
                onSelectFloor && onSelectFloor('')
                setDetailOpen(false)
              }}
            />
            <span>전체층</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="floorModeInTable"
              checked={mode === 'ONE'}
              onChange={() => {
                onSelectMode && onSelectMode('ONE')
                setDetailOpen(true)
              }}
            />
            <span>특정층</span>
          </label>
          <div className="min-w-[220px]">
            <select
              className="w-full rounded-md border px-3 py-2 text-sm disabled:bg-slate-50"
              value={selected}
              onChange={(e) => {
                onSelectMode && onSelectMode('ONE')
                onSelectFloor && onSelectFloor(e.target.value)
                setDetailOpen(true)
              }}
              disabled={floorOptions.length === 0}
            >
              <option value="">{floorOptions.length ? '층 선택' : '층 없음'}</option>
              {floorOptions.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <button
            type="button"
            className="ml-auto text-xs rounded border px-2 py-1 hover:bg-white"
            onClick={() => setDetailOpen((v) => !v)}
          >
            {detailOpen ? '상세 접기' : '상세 보기'}
          </button>
        </div>
        <div className="mt-1 text-[11px] text-slate-500">
          * 선택된 층(또는 전체)에 대해 용도/구조 기준으로 합산 요약을 먼저 보여줍니다.
        </div>
      </div>

      {floorSummary.length > 0 && (
        <div className="overflow-auto border-b">
          <table className="w-full text-sm">
            <thead className="bg-white text-slate-600">
              <tr className="border-b">
                <th className="text-left px-3 py-2 whitespace-nowrap">동</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">층</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">층구분</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">용도</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">구조</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">주/부속</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">면적제외</th>
                <th className="text-right px-3 py-2 whitespace-nowrap">면적합(㎡)</th>
                <th className="text-right px-3 py-2 whitespace-nowrap">건수</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {floorSummary.slice(0, 40).map((r, idx) => (
                <tr key={`${r.flrNoNm || 'flr'}-${idx}`} className="text-slate-900">
                  <td className="px-3 py-2 whitespace-nowrap">{r.dongNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.flrNoNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.flrGbCdNm || '-'}</td>
                  <td className="px-3 py-2">{r.mainPurpsCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.strctCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.mainAtchGbCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{String(r.areaExctYn || '').trim() === '1' ? '제외' : '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{r.areaSum ? r.areaSum.toFixed(3) : '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{r.count}</td>
                </tr>
              ))}
              {floorSummary.length > 40 && (
                <tr>
                  <td className="px-3 py-2 text-xs text-slate-500" colSpan={9}>
                    요약이 너무 많아 {floorSummary.length}건 중 40건만 표시했습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!detailOpen ? (
        <div className="px-3 py-3 text-sm text-slate-500">상세 보기를 누르면 원본 행을 확인할 수 있습니다.</div>
      ) : (
        view.length === 0 ? (
          <div className="px-3 py-3 text-sm text-slate-500">층별개요 정보가 없습니다.</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="text-left px-3 py-2 whitespace-nowrap">동</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">층</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">층구분</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">용도</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">구조</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">주/부속</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">면적제외</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">면적(㎡)</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {view.map((r, idx) => (
                  <tr key={`${r?.flrNoNm || 'flr'}-${idx}`} className="text-slate-900">
                    <td className="px-3 py-2 whitespace-nowrap">{r?.dongNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r?.flrNoNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r?.flrGbCdNm || '-'}</td>
                    <td className="px-3 py-2">{r?.mainPurpsCdNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r?.strctCdNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r?.mainAtchGbCdNm || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{String(r?.areaExctYn || '').trim() === '1' ? '제외' : '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">{r?.area ? formatNumber(r.area) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  )
}

function HousingPriceTable({ rows }) {
  const list = Array.isArray(rows) ? rows : []
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">주택가격(건축물대장, 참고)</div>
      {list.length === 0 ? (
        <div className="px-3 py-3 text-sm text-slate-500">주택가격 정보가 없습니다.</div>
      ) : (
        <div className="divide-y">
          {list.slice(0, 5).map((r, idx) => (
            <div key={`${r?.stdDay || 'std'}-${idx}`} className="flex items-start gap-3 px-3 py-2 text-sm">
              <div className="w-28 shrink-0 text-slate-500">
                <div className="text-[11px] font-semibold">기준일</div>
                <div className="mt-0.5">{formatDateDigits(r?.stdDay) || '-'}</div>
              </div>
              <div className="flex-1 text-slate-900 break-words">
                {r?.hsprc ? `${formatNumber(r.hsprc)} 원` : '-'}
                {r?.bylotCnt ? <span className="ml-2 text-xs text-slate-500">(부속지번수 {r.bylotCnt})</span> : null}
                {r?.crtnDay ? <div className="mt-0.5 text-[11px] text-slate-500">자료생성일: {formatDateDigits(r.crtnDay) || '-'}</div> : null}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="px-3 py-2 text-[11px] text-slate-500 border-t bg-slate-50">
        * 공시/평가 성격의 참고값이며, 보험가액(복구비용)을 직접 대체하지 않습니다.
        <span className="ml-2">* 기준일과 자료생성일은 서로 다를 수 있습니다.</span>
      </div>
    </div>
  )
}

function AtchJibunTable({ rows }) {
  const list = Array.isArray(rows) ? rows : []
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900">부속지번</div>
      {list.length === 0 ? (
        <div className="px-3 py-3 text-sm text-slate-500">부속지번 정보가 없습니다.</div>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-3 py-2 whitespace-nowrap">구분</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">대지구분</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">번</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">지</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">기타지번명</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {list.map((r, idx) => (
                <tr key={`${r?.atchBun || 'atch'}-${idx}`} className="text-slate-900">
                  <td className="px-3 py-2 whitespace-nowrap">{r?.atchRegstrGbCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.atchPlatGbCd || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.atchBun || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.atchJi || '-'}</td>
                  <td className="px-3 py-2">{r?.atchEtcJibunNm || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function JijiguTable({ rows }) {
  const [open, setOpen] = React.useState(false)
  const raw = Array.isArray(rows) ? rows : []
  const list = React.useMemo(() => {
    const seen = new Set()
    const out = []
    for (const r of raw) {
      const k = [
        String(r?.jijiguGbCdNm || '').trim(),
        String(r?.jijiguCdNm || '').trim(),
        String(r?.reprYn || '').trim(),
        String(r?.etcJijigu || '').trim(),
      ].join('|')
      if (seen.has(k)) continue
      seen.add(k)
      out.push(r)
    }
    return out
  }, [raw])
  return (
    <div className="rounded border bg-white">
      <div className="px-3 py-2 border-b text-sm font-semibold text-slate-900 flex items-center justify-between gap-2">
        <span>지역지구구역</span>
        {list.length > 12 && (
          <button
            type="button"
            className="text-xs rounded border px-2 py-1 hover:bg-slate-50"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? '접기' : `펼치기(${list.length}건)`}
          </button>
        )}
      </div>
      <div className="px-3 py-2 text-[11px] text-slate-500 border-b bg-slate-50">
        * 지역지구구역은 건축물에 적용되는 지정(용도지역/지구/구역 등)이 여러 개 겹칠 수 있어 다건으로 내려옵니다.
      </div>
      {list.length === 0 ? (
        <div className="px-3 py-3 text-sm text-slate-500">지역지구구역 정보가 없습니다.</div>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-3 py-2 whitespace-nowrap">구분</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">코드명</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">대표</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">비고</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(open ? list : list.slice(0, 12)).map((r, idx) => (
                <tr key={`${r?.jijiguCdNm || 'jijigu'}-${idx}`} className="text-slate-900">
                  <td className="px-3 py-2 whitespace-nowrap">{r?.jijiguGbCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.jijiguCdNm || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r?.reprYn || '-'}</td>
                  <td className="px-3 py-2">{r?.etcJijigu || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function FireBuildingRegistryPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const crmPrefillFromUrlRef = React.useRef(false)
  const restoringRef = React.useRef(false)
  const draftSaveTimerRef = React.useRef(null)
  const [activeTab, setActiveTab] = React.useState('LOOKUP') // LOOKUP | HISTORY
  const [currentStep, setCurrentStep] = React.useState(1) // 1~4
  const [addr, setAddr] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [result, setResult] = React.useState(null)
  const [error, setError] = React.useState(null)
  const [detailOpen, setDetailOpen] = React.useState(false)
  const [designOpen, setDesignOpen] = React.useState(false)
  const [customerName, setCustomerName] = React.useState('')
  const [customerPhone, setCustomerPhone] = React.useState('')
  const [customerRefId, setCustomerRefId] = React.useState(null)
  const [commonExistingCustomer, setCommonExistingCustomer] = React.useState(false)
  const [amtBuilding, setAmtBuilding] = React.useState('')
  const [amtHousehold, setAmtHousehold] = React.useState('')
  const [amtContents, setAmtContents] = React.useState('')
  const [amtFacility, setAmtFacility] = React.useState('')
  const [amtMovables, setAmtMovables] = React.useState('')
  const [amtMachinery, setAmtMachinery] = React.useState('')
  const [selectedDongNm, setSelectedDongNm] = React.useState('')
  const [selectedHoNm, setSelectedHoNm] = React.useState('')
  const [floorSelectMode, setFloorSelectMode] = React.useState('ALL') // ALL | ONE
  const [selectedFloorNoNm, setSelectedFloorNoNm] = React.useState('')
  const [selectedBizTypeId, setSelectedBizTypeId] = React.useState('')
  const [checkedEndorsements, setCheckedEndorsements] = React.useState([])
  const [fireOccurrenceStats, setFireOccurrenceStats] = React.useState(null)
  const [fireOccurrenceLoading, setFireOccurrenceLoading] = React.useState(false)
  const [fireOccurrenceError, setFireOccurrenceError] = React.useState('')

  const [historyLoading, setHistoryLoading] = React.useState(false)
  const [historyError, setHistoryError] = React.useState(null)
  const [historyQ, setHistoryQ] = React.useState('')
  const [historyRows, setHistoryRows] = React.useState([])
  const [currentHistoryId, setCurrentHistoryId] = React.useState(null)

  const roadAddress = addr?.roadAddress || ''
  const jibunAddress = addr?.jibunAddress || ''
  const bcode = addr?.bcode || '' // 법정동코드(10자리) - JUSO 없이 조회할 때 핵심

  const hasAddrSelected = !!(roadAddress || jibunAddress || bcode)
  const lookupOk = !!result?.ok
  const canSearchAddress = !hasAddrSelected && !loading && !lookupOk
  const canLookupBuilding = hasAddrSelected && !loading && !lookupOk
  const canReset = hasAddrSelected
    || !!result
    || !!String(customerName || '').trim()
    || !!String(customerPhone || '').trim()
    || !!String(amtBuilding || '').trim()
    || !!String(amtHousehold || '').trim()
    || !!String(amtContents || '').trim()
    || !!String(amtFacility || '').trim()
    || !!String(amtMovables || '').trim()
    || !!String(amtMachinery || '').trim()
    || !!String(selectedDongNm || '').trim()
    || !!String(selectedHoNm || '').trim()
    || (floorSelectMode === 'ONE' && !!String(selectedFloorNoNm || '').trim())
    || !!String(selectedBizTypeId || '').trim()
    || (Array.isArray(checkedEndorsements) && checkedEndorsements.length > 0)

  const DRAFT_KEY = 'fire_building_registry_draft_v1'

  React.useEffect(() => {
    if (crmPrefillFromUrlRef.current) return
    const raw = searchParams.get('customerRefId')
    if (!raw) return
    const id = Number(String(raw).trim())
    if (!Number.isFinite(id) || id <= 0) return
    crmPrefillFromUrlRef.current = true
    ;(async () => {
      try {
        const res = await api.get(`/customer-management/customers/${id}/summary`, { __silent: true })
        const data = res?.data?.data ?? res?.data
        setCustomerRefId(id)
        if (data?.customerName) {
          setCustomerName(String(data.customerName).trim())
          setCommonExistingCustomer(true)
        }
        if (data?.customerPhone) {
          setCustomerPhone(String(data.customerPhone))
        }
        setActiveTab('LOOKUP')
        const next = new URLSearchParams(searchParams)
        next.delete('customerRefId')
        setSearchParams(next, { replace: true })
      } catch (_) {
        crmPrefillFromUrlRef.current = false
      }
    })()
  }, [searchParams, setSearchParams])

  // 임시저장 복원(뒤로가기/새로고침 대비)
  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY)
      if (!raw) return
      const data = JSON.parse(raw)
      if (!data || typeof data !== 'object') return

      restoringRef.current = true
      if (data.activeTab === 'HISTORY') setActiveTab('HISTORY')
      if (data.currentStep != null) {
        const n = Number(data.currentStep)
        if (Number.isFinite(n) && n >= 1 && n <= 4) setCurrentStep(n)
      }

      const a = data.addr || null
      if (a && (a.roadAddress || a.jibunAddress || a.bcode)) {
        setAddr({
          roadAddress: a.roadAddress || '',
          jibunAddress: a.jibunAddress || '',
          bcode: a.bcode || '',
        })
      }
      if (data.customerName !== undefined) setCustomerName(String(data.customerName || ''))
      if (data.customerPhone !== undefined) setCustomerPhone(String(data.customerPhone || ''))
      if (data.customerRefId !== undefined) {
        const id = Number(data.customerRefId)
        setCustomerRefId(Number.isFinite(id) && id > 0 ? id : null)
      }
      if (data.commonExistingCustomer !== undefined) setCommonExistingCustomer(!!data.commonExistingCustomer)
      if (data.amtBuilding !== undefined) setAmtBuilding(String(data.amtBuilding || ''))
      if (data.amtHousehold !== undefined) setAmtHousehold(String(data.amtHousehold || ''))
      if (data.amtContents !== undefined) setAmtContents(String(data.amtContents || ''))
      if (data.amtFacility !== undefined) setAmtFacility(String(data.amtFacility || ''))
      if (data.amtMovables !== undefined) setAmtMovables(String(data.amtMovables || ''))
      if (data.amtMachinery !== undefined) setAmtMachinery(String(data.amtMachinery || ''))
      if (data.selectedDongNm !== undefined) setSelectedDongNm(String(data.selectedDongNm || ''))
      if (data.selectedHoNm !== undefined) setSelectedHoNm(String(data.selectedHoNm || ''))
      if (data.floorSelectMode !== undefined) setFloorSelectMode(data.floorSelectMode === 'ONE' ? 'ONE' : 'ALL')
      if (data.selectedFloorNoNm !== undefined) setSelectedFloorNoNm(String(data.selectedFloorNoNm || ''))
      if (data.selectedBizTypeId !== undefined) setSelectedBizTypeId(String(data.selectedBizTypeId || ''))
      if (Array.isArray(data.checkedEndorsements)) {
        setCheckedEndorsements(data.checkedEndorsements.map((x) => String(x || '').trim()).filter(Boolean))
      }

      // 주소 변경 effect가 복원값을 지우지 않도록 다음 tick까지 유지
      setTimeout(() => { restoringRef.current = false }, 0)
    } catch (e) {
      // 임시저장 복원 실패는 기능을 막지 않음
      restoringRef.current = false
    }
  }, [])

  // 임시저장 자동 저장(디바운스)
  React.useEffect(() => {
    if (restoringRef.current) return
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current)
    draftSaveTimerRef.current = setTimeout(() => {
      try {
        const payload = {
          v: 1,
          updatedAt: Date.now(),
          activeTab,
          currentStep,
          addr: addr ? { roadAddress, jibunAddress, bcode } : null,
          customerName,
          customerPhone,
          customerRefId,
          commonExistingCustomer,
          amtBuilding,
          amtHousehold,
          amtContents,
          amtFacility,
          amtMovables,
          amtMachinery,
          selectedDongNm,
          selectedHoNm,
          floorSelectMode,
          selectedFloorNoNm,
          selectedBizTypeId,
          checkedEndorsements: Array.isArray(checkedEndorsements) ? checkedEndorsements : [],
        }
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(payload))
      } catch (e) {
        // 저장 실패는 무시
      }
    }, 350)
    return () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current)
    }
  }, [
    addr,
    roadAddress, jibunAddress, bcode,
    customerName, customerPhone, customerRefId, commonExistingCustomer,
    amtBuilding, amtHousehold, amtContents, amtFacility, amtMovables, amtMachinery,
    selectedDongNm, selectedHoNm,
    floorSelectMode, selectedFloorNoNm,
    selectedBizTypeId, checkedEndorsements,
    activeTab,
    currentStep,
  ])

  function resetAll() {
    try { sessionStorage.removeItem(DRAFT_KEY) } catch { }
    restoringRef.current = true
    setActiveTab('LOOKUP')
    setCurrentStep(1)
    setAddr(null)
    setResult(null)
    setError(null)
    setDetailOpen(false)
    setDesignOpen(false)
    setCustomerName('')
    setCustomerPhone('')
    setCustomerRefId(null)
    setCommonExistingCustomer(false)
    setAmtBuilding('')
    setAmtHousehold('')
    setAmtContents('')
    setAmtFacility('')
    setAmtMovables('')
    setAmtMachinery('')
    setSelectedDongNm('')
    setSelectedHoNm('')
    setFloorSelectMode('ALL')
    setSelectedFloorNoNm('')
    setSelectedBizTypeId('')
    setCheckedEndorsements([])
    setFireOccurrenceStats(null)
    setFireOccurrenceLoading(false)
    setFireOccurrenceError('')
    setHistoryQ('')
    setHistoryRows([])
    setHistoryError(null)
    setCurrentHistoryId(null)
    setTimeout(() => { restoringRef.current = false }, 0)
  }

  // 조회 성공 시 스텝 2 이상으로 이동(최소 보정)
  React.useEffect(() => {
    if (!lookupOk) return
    if (currentStep < 2) setCurrentStep(2)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupOk])

  function summaryFromResult(data) {
    const titleItem = data?.titleItem || null
    const basisItem = data?.basisItem || null
    const purps = pick(titleItem, ['mainPurpsCdNm', 'mainPurpsCd', 'etcPurps', 'etcPurpsNm'])
      || pick(basisItem, ['mainPurpsCdNm', 'mainPurpsCd', 'etcPurps', 'etcPurpsNm'])
    const strct = pick(titleItem, ['strctCdNm', 'strctCd'])
      || pick(basisItem, ['strctCdNm', 'strctCd'])
    const useApr = formatDateDigits(pick(titleItem, ['useAprDay', 'pmsDay', 'useAprDt']))
      || formatDateDigits(pick(basisItem, ['useAprDay', 'pmsDay', 'useAprDt']))
    const totArea = pick(basisItem, ['totArea', 'totarea', 'totar'])
      || pick(titleItem, ['totArea', 'totarea', 'totar'])
    const grnd = pick(basisItem, ['grndFlrCnt', 'grndFlrCo', 'grndFlr'])
      || pick(titleItem, ['grndFlrCnt', 'grndFlrCo', 'grndFlr'])
    const ugrnd = pick(basisItem, ['ugrndFlrCnt', 'ugrndFlrCo', 'ugrndFlr'])
      || pick(titleItem, ['ugrndFlrCnt', 'ugrndFlrCo', 'ugrndFlr'])
    const bldNm = pick(titleItem, ['bldNm', 'bldNmNm'])
      || pick(basisItem, ['bldNm'])
    return { bldNm, purps, strct, useApr, totArea, grnd, ugrnd }
  }

  async function saveLookupHistory({ lookupPayload, resultData }) {
    try {
      if (!resultData?.ok) return
      const bt = FIRE_BIZ_TYPES.find((t) => t.id === String(selectedBizTypeId || '').trim())
      const bizTypeLabel = bt?.label || null
      const endorsements = Array.isArray(checkedEndorsements)
        ? checkedEndorsements.map((x) => String(x || '').trim()).filter(Boolean)
        : []

      const sum = summaryFromResult(resultData)

      const res = await api.post('/fire/building-registry/history', {
        roadAddress: lookupPayload?.roadAddress || roadAddress || null,
        jibunAddress: lookupPayload?.jibunAddress || jibunAddress || null,
        bcode: lookupPayload?.bcode || bcode || null,
        customerName: String(customerName || '').trim() || null,
        customerPhone: String(customerPhone || '').trim() || null,
        customerRefId: customerRefId || null,
        existingCustomer: !!commonExistingCustomer,
        amtBuilding: parseMoneyInput(amtBuilding),
        amtHousehold: parseMoneyInput(amtHousehold),
        amtContents: parseMoneyInput(amtContents),
        amtFacility: parseMoneyInput(amtFacility),
        amtMovables: parseMoneyInput(amtMovables),
        amtMachinery: parseMoneyInput(amtMachinery),
        selectedDongNm: lookupPayload?.dongNm || selectedDongNm || null,
        selectedHoNm: lookupPayload?.hoNm || selectedHoNm || null,
        floorSelectMode: floorSelectMode || 'ALL',
        selectedFloorNoNm: String(selectedFloorNoNm || '').trim() || null,
        bizTypeId: String(selectedBizTypeId || '').trim() || null,
        bizTypeLabel,
        endorsements,
        bldNm: sum?.bldNm || null,
        purps: sum?.purps || null,
        strct: sum?.strct || null,
        useApr: sum?.useApr || null,
        totArea: sum?.totArea != null ? String(sum.totArea) : null,
        grnd: sum?.grnd != null ? String(sum.grnd) : null,
        ugrnd: sum?.ugrnd != null ? String(sum.ugrnd) : null,
      })
      const payload = res?.data?.data ?? res?.data ?? null
      const hid = payload != null ? Number(payload) : null
      if (Number.isFinite(hid) && hid > 0) setCurrentHistoryId(hid)
    } catch (e) {
      // 이력 저장 실패가 조회 UX를 막지 않도록 한다.
    }
  }

  async function performLookup(lookupPayload, { skipHistorySave } = {}) {
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const payload = lookupPayload || {
        roadAddress: roadAddress || null,
        jibunAddress: jibunAddress || null,
        bcode: bcode || null,
        dongNm: selectedDongNm || null,
        hoNm: selectedHoNm || null,
      }
      const res = await api.post('/fire/building-registry/lookup', payload)
      const data = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setResult(data)
      if (!data?.ok) {
        setError(data?.message || '조회 실패')
      } else if (!skipHistorySave) {
        // 성공 조회는 이력으로 자동 저장(실패해도 UX 영향 없음)
        saveLookupHistory({ lookupPayload: payload, resultData: data })
      }
    } catch (e) {
      const serverMsg =
        e?.response?.data?.message
        || e?.response?.data?.error
        || e?.response?.data?.detail
        || e?.response?.data?.msg
      setError(serverMsg || e?.message || '조회 실패')
    } finally {
      setLoading(false)
    }
  }

  async function onLookup() {
    return performLookup(null)
  }

  async function fetchHistoryList(nextQ) {
    setHistoryLoading(true)
    setHistoryError(null)
    try {
      const res = await api.get('/fire/building-registry/history', {
        params: {
          q: String(nextQ ?? historyQ ?? '').trim() || undefined,
          limit: 50,
        },
      })
      const data = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setHistoryRows(Array.isArray(data) ? data : [])
    } catch (e) {
      setHistoryError(e?.response?.data?.message || e?.message || '이력 조회 실패')
    } finally {
      setHistoryLoading(false)
    }
  }

  async function loadHistoryAndRestore(id, { openDesign } = {}) {
    if (!id) return
    setHistoryLoading(true)
    setHistoryError(null)
    try {
      setCurrentHistoryId(id)
      const res = await api.get(`/fire/building-registry/history/${id}`)
      const data = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if (!data) {
        setHistoryError('이력 데이터를 찾을 수 없습니다.')
        return
      }

      restoringRef.current = true
      setActiveTab('LOOKUP')
      setCurrentStep(2)
      setAddr({
        roadAddress: data.roadAddress || '',
        jibunAddress: data.jibunAddress || '',
        bcode: data.bcode || '',
      })
      setCustomerName(String(data.customerName || ''))
      setCustomerPhone(String(data.customerPhoneDigits || data.customerPhone || ''))
      setCustomerRefId(data.customerRefId || null)
      setCommonExistingCustomer(!!data.existingCustomer)
      setAmtBuilding(data.amtBuilding != null ? formatMoney(data.amtBuilding) : '')
      setAmtHousehold(data.amtHousehold != null ? formatMoney(data.amtHousehold) : '')
      setAmtContents(data.amtContents != null ? formatMoney(data.amtContents) : '')
      setAmtFacility(data.amtFacility != null ? formatMoney(data.amtFacility) : '')
      setAmtMovables(data.amtMovables != null ? formatMoney(data.amtMovables) : '')
      setAmtMachinery(data.amtMachinery != null ? formatMoney(data.amtMachinery) : '')
      setSelectedDongNm(String(data.selectedDongNm || ''))
      setSelectedHoNm(String(data.selectedHoNm || ''))
      setFloorSelectMode(data.floorSelectMode === 'ONE' ? 'ONE' : 'ALL')
      setSelectedFloorNoNm(String(data.selectedFloorNoNm || ''))
      setSelectedBizTypeId(String(data.bizTypeId || ''))
      setCheckedEndorsements(Array.isArray(data.endorsements) ? data.endorsements : [])
      setResult(null)
      setError(null)

      setTimeout(() => { restoringRef.current = false }, 0)

      // 이력 기반 재조회(데이터 화면 재구성)
      await performLookup({
        roadAddress: data.roadAddress || null,
        jibunAddress: data.jibunAddress || null,
        bcode: data.bcode || null,
        dongNm: data.selectedDongNm || null,
        hoNm: data.selectedHoNm || null,
      }, { skipHistorySave: true })

      if (openDesign) setDesignOpen(true)
    } catch (e) {
      setHistoryError(e?.response?.data?.message || e?.message || '이력 불러오기 실패')
    } finally {
      setHistoryLoading(false)
    }
  }

  React.useEffect(() => {
    if (activeTab !== 'HISTORY') return
    fetchHistoryList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // 주소를 다시 선택하면 전유부 선택은 초기화
  React.useEffect(() => {
    if (restoringRef.current) return
    setSelectedDongNm('')
    setSelectedHoNm('')
    setFloorSelectMode('ALL')
    setSelectedFloorNoNm('')
    setSelectedBizTypeId('')
    setCheckedEndorsements([])
  }, [roadAddress, jibunAddress, bcode])

  // 동/호 적용(전유부 선택) 후: 전유부 응답의 층 정보를 이용해 '선택 층'을 자동 세팅
  React.useEffect(() => {
    if (!result?.ok) return
    const d = String(result?.selectedDongNm || '').trim()
    const h = String(result?.selectedHoNm || '').trim()
    if (!d || !h) return

    const rows = Array.isArray(result?.unitAreaRows) ? result.unitAreaRows : []
    if (rows.length === 0) return

    // 전유 행의 층을 우선 사용(없으면 첫 유효 층)
    const pickFloor = () => {
      const prefer = rows.find((r) => String(r?.exposPubuseGbCdNm || '').includes('전유') && String(r?.flrNoNm || '').trim())
      if (prefer) return String(prefer.flrNoNm || '').trim()
      const any = rows.find((r) => String(r?.flrNoNm || '').trim())
      return any ? String(any.flrNoNm || '').trim() : ''
    }

    const flr = pickFloor()
    if (!flr) return
    // "각층" 같은 값은 선택층으로 부적합
    if (flr.includes('각층')) return

    // 사용자가 이미 특정층을 선택해두지 않았다면 자동 세팅
    const already = String(selectedFloorNoNm || '').trim()
    if (floorSelectMode === 'ONE' && already) return

    setFloorSelectMode('ONE')
    setSelectedFloorNoNm(flr)
  }, [result, floorSelectMode, selectedFloorNoNm])

  const unitOptions = React.useMemo(() => {
    return Array.isArray(result?.unitOptions) ? result.unitOptions : []
  }, [result?.unitOptions])

  const floorRows = React.useMemo(() => {
    return Array.isArray(result?.floorRows) ? result.floorRows : []
  }, [result?.floorRows])
  const housingPriceRows = React.useMemo(() => {
    return Array.isArray(result?.housingPriceRows) ? result.housingPriceRows : []
  }, [result?.housingPriceRows])
  const atchJibunRows = React.useMemo(() => {
    return Array.isArray(result?.atchJibunRows) ? result.atchJibunRows : []
  }, [result?.atchJibunRows])
  const jijiguRows = React.useMemo(() => {
    return Array.isArray(result?.jijiguRows) ? result.jijiguRows : []
  }, [result?.jijiguRows])

  const floorOptions = React.useMemo(() => {
    const set = new Set()
    for (const r of floorRows) {
      const v = String(r?.flrNoNm || '').trim()
      if (v) set.add(v)
    }
    return Array.from(set).sort(compareFloorName)
  }, [floorRows])
  const dongOptions = React.useMemo(() => {
    const set = new Set()
    for (const u of unitOptions) {
      const d = String(u?.dongNm || '').trim()
      if (d) set.add(d)
    }
    return Array.from(set).sort(compareDongHo)
  }, [unitOptions])
  const hoOptions = React.useMemo(() => {
    const d = String(selectedDongNm || '').trim()
    if (!d) return []
    const set = new Set()
    for (const u of unitOptions) {
      if (String(u?.dongNm || '').trim() !== d) continue
      const h = String(u?.hoNm || '').trim()
      if (h) set.add(h)
    }
    return Array.from(set).sort(compareDongHo)
  }, [unitOptions, selectedDongNm])

  const titleItem = result?.titleItem || null
  const basisItem = result?.basisItem || null

  const summary = React.useMemo(() => {
    const purps = pick(titleItem, ['mainPurpsCdNm', 'mainPurpsCd', 'etcPurps', 'etcPurpsNm'])
      || pick(basisItem, ['mainPurpsCdNm', 'mainPurpsCd', 'etcPurps', 'etcPurpsNm'])
    const strct = pick(titleItem, ['strctCdNm', 'strctCd'])
      || pick(basisItem, ['strctCdNm', 'strctCd'])
    const useApr = formatDateDigits(pick(titleItem, ['useAprDay', 'pmsDay', 'useAprDt']))
      || formatDateDigits(pick(basisItem, ['useAprDay', 'pmsDay', 'useAprDt']))
    const totArea = pick(basisItem, ['totArea', 'totarea', 'totar'])
      || pick(titleItem, ['totArea', 'totarea', 'totar'])
    const grnd = pick(basisItem, ['grndFlrCnt', 'grndFlrCo', 'grndFlr'])
      || pick(titleItem, ['grndFlrCnt', 'grndFlrCo', 'grndFlr'])
    const ugrnd = pick(basisItem, ['ugrndFlrCnt', 'ugrndFlrCo', 'ugrndFlr'])
      || pick(titleItem, ['ugrndFlrCnt', 'ugrndFlrCo', 'ugrndFlr'])
    const bldNm = pick(titleItem, ['bldNm', 'bldNmNm'])
      || pick(basisItem, ['bldNm'])
    return { bldNm, purps, strct, useApr, totArea, grnd, ugrnd }
  }, [titleItem, basisItem])

  const recommendedBizTypeIds = React.useMemo(() => {
    const purposeText = [
      summary?.purps,
      pick(titleItem, ['etcPurps', 'etcPurpsNm', 'mainPurpsCdNm']),
      pick(basisItem, ['etcPurps', 'etcPurpsNm', 'mainPurpsCdNm']),
    ].filter(Boolean).join(' ')
    const ids = recommendBizTypesFromPurposeText(purposeText)
    // '기타'는 추천 대상으로 포함하지 않는다.
    return ids.filter((id) => id !== 'OTHER')
  }, [summary?.purps, titleItem, basisItem])

  const selectedBizType = React.useMemo(() => {
    const id = String(selectedBizTypeId || '').trim()
    return FIRE_BIZ_TYPES.find((t) => t.id === id) || null
  }, [selectedBizTypeId])

  const fireRegion = React.useMemo(() => {
    return extractKoreanRegion(roadAddress, jibunAddress)
  }, [roadAddress, jibunAddress])

  const requiredEndorsements = React.useMemo(() => {
    return selectedBizType?.endorsements || []
  }, [selectedBizType])

  const fireOccurrenceRecommendation = React.useMemo(() => {
    return buildFireOccurrenceRecommendation(fireOccurrenceStats, selectedBizType)
  }, [fireOccurrenceStats, selectedBizType])

  const fireOccurrenceRecommendedEndorsements = fireOccurrenceRecommendation.endorsements
  const fireOccurrenceRecommendationKey = fireOccurrenceRecommendedEndorsements.join('|')

  React.useEffect(() => {
    if (!result?.ok || currentStep !== 4 || !fireRegion.sido) {
      setFireOccurrenceStats(null)
      setFireOccurrenceError('')
      return
    }

    let alive = true
    setFireOccurrenceLoading(true)
    setFireOccurrenceError('')
    ;(async () => {
      try {
        const res = await api.get('/fire/occurrence-stats', {
          params: {
            sido: fireRegion.sido || undefined,
            sigungu: fireRegion.sigungu || undefined,
            limit: 5,
          },
          __silent: true,
        })
        const data = res?.data?.data ?? res?.data
        if (!alive) return
        setFireOccurrenceStats(data || null)
      } catch (e) {
        if (!alive) return
        setFireOccurrenceStats(null)
        setFireOccurrenceError('지역 화재 통계를 불러오지 못했습니다.')
      } finally {
        if (alive) setFireOccurrenceLoading(false)
      }
    })()

    return () => {
      alive = false
    }
  }, [result?.ok, currentStep, fireRegion.sido, fireRegion.sigungu])

  // 조회 결과가 들어왔을 때(성공), 업종/특약이 비어 있으면 자동 추천/체크
  React.useEffect(() => {
    if (!result?.ok) return
    const cur = String(selectedBizTypeId || '').trim()
    if (cur) return
    if (!recommendedBizTypeIds || recommendedBizTypeIds.length === 0) return
    const rec = recommendedBizTypeIds[0]
    const bt = FIRE_BIZ_TYPES.find((t) => t.id === rec)
    if (!bt) return
    setSelectedBizTypeId(bt.id)
    setCheckedEndorsements(mergeEndorsements(bt.endorsements || [], fireOccurrenceRecommendedEndorsements))
  }, [result, selectedBizTypeId, recommendedBizTypeIds, fireOccurrenceRecommendationKey, fireOccurrenceRecommendedEndorsements])

  React.useEffect(() => {
    if (!result?.ok || !selectedBizType || fireOccurrenceRecommendedEndorsements.length === 0) return
    setCheckedEndorsements((prev) => {
      const current = Array.isArray(prev) ? prev : []
      const required = selectedBizType.endorsements || []
      const currentIsTypeDefault = required.length > 0
        && current.length === required.length
        && required.every((name) => current.includes(name))
      if (!currentIsTypeDefault) return current
      return mergeEndorsements(current, fireOccurrenceRecommendedEndorsements)
    })
  }, [result?.ok, selectedBizType, fireOccurrenceRecommendationKey, fireOccurrenceRecommendedEndorsements])

  function buildPrefill() {
    const cleanName = String(customerName || '').trim()
    const title = `${cleanName} 고객님 화재보험 설계 요청드립니다.`
    const amounts = {
      building: parseMoneyInput(amtBuilding),
      household: parseMoneyInput(amtHousehold),
      contents: parseMoneyInput(amtContents),
      facility: parseMoneyInput(amtFacility),
      movables: parseMoneyInput(amtMovables),
      machinery: parseMoneyInput(amtMachinery),
    }
    const total = Object.values(amounts).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0)
    const bizTypeLabel = selectedBizType?.label || null
    const selectedEndorsements = Array.isArray(checkedEndorsements)
      ? checkedEndorsements.map((s) => String(s || '').trim()).filter(Boolean)
      : []

    const lines = [
      `- 도로명주소: ${roadAddress || '-'}`,
      `- 지번주소: ${jibunAddress || '-'}`,
      ...(result?.selectedDongNm && result?.selectedHoNm ? [
        '',
        '[전유부(선택)]',
        `- 동/호: ${result.selectedDongNm} / ${result.selectedHoNm}`,
        ...(() => {
          const rows = Array.isArray(result?.unitAreaRows) ? result.unitAreaRows : []
          if (rows.length === 0) return ['- 전유/공용면적: -']

          const sum = (() => {
            let ex = 0
            let pub = 0
            for (const r of rows) {
              const gb = String(r?.exposPubuseGbCdNm || '').trim()
              const n = Number(String(r?.area || '').replace(/,/g, '').trim())
              if (!Number.isFinite(n)) continue
              if (gb.includes('전유')) ex += n
              else if (gb.includes('공용')) pub += n
            }
            return { ex, pub }
          })()

          const expos = rows.find((r) => String(r?.exposPubuseGbCdNm || '').includes('전유'))
            || rows.find((r) => String(r?.flrNoNm || '').trim())
            || rows[0]
          const exposLine = (() => {
            if (!expos) return null
            const gb = String(expos?.exposPubuseGbCdNm || '').trim() || '전유'
            const flr = String(expos?.flrNoNm || '').trim()
            const flrGb = String(expos?.flrGbCdNm || '').trim()
            const purp = String(expos?.purpsNm || expos?.mainPurpsCdNm || '').trim()
            const strct = String(expos?.strctCdNm || '').trim()
            const area = expos?.area ? `${formatNumber(expos.area)} ㎡` : '-'
            // 요청 형식: "- 전유 / 층:3층 / (지상) / 용도:... / 구조:... / 면적:... ㎡"
            const parts = [
              `- ${gb}`,
              flr ? `층:${flr}` : null,
              flrGb ? `(${flrGb})` : null,
              purp ? `용도:${purp}` : null,
              strct ? `구조:${strct}` : null,
              `면적:${area}`,
            ].filter(Boolean)
            return parts.join(' / ')
          })()

          return [
            `- 전유 합계: ${sum.ex ? sum.ex.toFixed(3) : '-'} ㎡`,
            `- 공용 합계: ${sum.pub ? sum.pub.toFixed(3) : '-'} ㎡`,
            ...(exposLine ? [exposLine] : []),
          ]
        })(),
      ] : []),
      '',
      '[보험가액(입력, 만원)]',
      `- 건물: ${amounts.building != null ? `${formatMoney(amounts.building)} 만원` : '-'}`,
      `- 가재: ${amounts.household != null ? `${formatMoney(amounts.household)} 만원` : '-'}`,
      `- 집기: ${amounts.contents != null ? `${formatMoney(amounts.contents)} 만원` : '-'}`,
      `- 시설: ${amounts.facility != null ? `${formatMoney(amounts.facility)} 만원` : '-'}`,
      `- 동산: ${amounts.movables != null ? `${formatMoney(amounts.movables)} 만원` : '-'}`,
      `- 기계: ${amounts.machinery != null ? `${formatMoney(amounts.machinery)} 만원` : '-'}`,
      `- 합계: ${total > 0 ? `${formatMoney(total)} 만원` : '-'}`,
      ...(bizTypeLabel ? [
        '',
        '[가입 유형(선택)]',
        `- ${bizTypeLabel}`,
      ] : []),
      ...(selectedEndorsements.length > 0 ? [
        '',
        '아래 특약들은 포함 부탁드립니다.',
        ...selectedEndorsements.map((e) => {
          // 메모 표시명 보정(요청 포맷)
          if (e === '가전제품고장수리비용') return '- 가전제품고장수리비용'
          return `- ${e}`
        }),
      ] : []),
    ]
    return {
      source: 'FIRE',
      customerName: cleanName,
      customerPhone: String(customerPhone || '').trim(),
      customerRefId: customerRefId || null,
      title,
      commonNote: lines.join('\n'),
      commonExistingCustomer: !!commonExistingCustomer,
      fireAmounts: amounts,
      fireBizType: selectedBizTypeId || null,
      fireBizTypeLabel: bizTypeLabel,
      fireEndorsements: selectedEndorsements,
      fireBuildingHistoryId: currentHistoryId,
    }
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900">화재보험설계(건축물대장 조회)</h1>
            <MenuGuideButton slug="fire-insurance" title="화재보험 설계 가이드" />
          </div>
          {/* <p className="mt-1 text-sm text-slate-600">
            주소를 선택하면 서버에서(카카오 bcode+지번 기반으로) 건축물대장 API를 호출합니다. (JUSO는 추후 옵션)
          </p> */}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant={activeTab === 'LOOKUP' ? 'sgez' : 'sgezGhost'}
          size="sm"
          onClick={() => setActiveTab('LOOKUP')}
        >
          조회
        </Button>
        <Button
          type="button"
          variant={activeTab === 'HISTORY' ? 'sgez' : 'sgezGhost'}
          size="sm"
          onClick={() => setActiveTab('HISTORY')}
        >
          이력
        </Button>
        <Button
          type="button"
          disabled={!canReset}
          variant="sgezOutline"
          size="sm"
          onClick={resetAll}
        >
          초기화
        </Button>
        <div className="ml-auto text-[11px] text-slate-500">
          * 입력 내용은 자동 임시저장됩니다.
        </div>
      </div>

      {activeTab === 'LOOKUP' ? (
        <>
          <div className="mt-4 rounded-xl border bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold text-slate-900">진행 단계</div>
              <div className="text-[11px] text-slate-500">
                {lookupOk ? '조회 완료' : hasAddrSelected ? '주소 선택됨' : '주소 선택 필요'}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-4">
              {[
                { n: 1, title: '주소/기본정보' },
                { n: 2, title: '건축물대장 조회' },
                { n: 3, title: '동/호·층(선택)' },
                { n: 4, title: '설계의뢰' },
              ].map((s) => {
                const enabled =
                  s.n === 1
                  || (s.n === 2 && hasAddrSelected)
                  || (s.n === 3 && lookupOk)
                  || (s.n === 4 && lookupOk)
                const active = currentStep === s.n
                return (
                  <button
                    key={s.n}
                    type="button"
                    disabled={!enabled}
                    className={[
                      'rounded-md border px-3 py-2 text-sm text-left disabled:opacity-50',
                      active ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : enabled ? 'hover:bg-slate-50 bg-white' : 'bg-white',
                    ].join(' ')}
                    onClick={() => setCurrentStep(s.n)}
                  >
                    <div className="text-[11px] font-semibold opacity-90">STEP {s.n}</div>
                    <div className="mt-0.5 font-medium">{s.title}</div>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="mt-6 rounded-xl border bg-white p-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  disabled={!canSearchAddress}
                  variant={canSearchAddress ? 'sgez' : 'sgezOutline'}
                  size="sm"
                  onClick={() => {
                    try {
                      openDaumPostcode((data) => {
                        setAddr(data || null)
                        setResult(null)
                        setError(null)
                        setSelectedDongNm('')
                        setSelectedHoNm('')
                        setCurrentStep(1)
                      })
                    } catch (e) {
                      setError('주소검색 스크립트 로드에 실패했습니다.')
                    }
                  }}
                >
                  주소 검색
                </Button>

                <Button
                  type="button"
                  disabled={!canLookupBuilding}
                  variant={canLookupBuilding ? 'sgez' : 'sgezOutline'}
                  size="sm"
                  onClick={() => {
                    setCurrentStep(2)
                    onLookup()
                  }}
                >
                  {loading ? '조회 중...' : '건축물대장 조회'}
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold text-slate-500">도로명주소</div>
              <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-800">
                {roadAddress || '-'}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">지번주소</div>
              <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-800">
                {jibunAddress || '-'}
              </div>
            </div>
            {currentStep === 1 ? (
            <>
            <div>
              <div className="text-xs font-semibold text-slate-500">고객명</div>
              <ManagedCustomerAutocomplete
                value={customerName}
                onValueChange={(next) => {
                  setCustomerName(next)
                  setCustomerRefId(null)
                }}
                selectedCustomerId={customerRefId}
                onSelectCustomer={(customer) => {
                  setCustomerRefId(customer.id)
                  setCustomerName(customer.customerName != null ? String(customer.customerName).trim() : '')
                  if (customer.customerPhone) setCustomerPhone(String(customer.customerPhone))
                  setCommonExistingCustomer(true)
                }}
                onClearCustomer={() => setCustomerRefId(null)}
                inputClassName="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                placeholder="예) 홍길동"
              />
              <div className="mt-1 text-[11px] text-slate-500">
                * 설계의뢰 생성 시 자동으로 포함됩니다.
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">고객 전화번호</div>
              <input
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                placeholder="예) 01012345678"
                value={customerPhone}
                onChange={(e) => {
                  setCustomerPhone(e.target.value)
                  setCustomerRefId(null)
                }}
              />
              <div className="mt-1 text-[11px] text-slate-500">
                {customerRefId ? '고객관리 기존 고객과 연결됩니다.' : '전화번호가 있으면 고객관리와 자동 연결됩니다.'}
              </div>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={commonExistingCustomer}
                  onChange={(e) => setCommonExistingCustomer(e.target.checked)}
                />
                <span>가입설계동의 여부</span>
              </label>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">보험가액(입력, 만원)</div>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-4">
                {[
                  { key: 'building', label: '건물', value: amtBuilding, set: setAmtBuilding },
                  { key: 'household', label: '가재', value: amtHousehold, set: setAmtHousehold },
                  { key: 'contents', label: '집기', value: amtContents, set: setAmtContents },
                  { key: 'facility', label: '시설', value: amtFacility, set: setAmtFacility },
                  { key: 'movables', label: '동산', value: amtMovables, set: setAmtMovables },
                  { key: 'machinery', label: '기계', value: amtMachinery, set: setAmtMachinery },
                ].map((f) => (
                  <div key={f.key}>
                    <div className="text-[11px] font-semibold text-slate-500">{f.label}</div>
                    <input
                      className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                      inputMode="numeric"
                      placeholder="0 (만원)"
                      value={f.value}
                      onChange={(e) => {
                        const raw = e.target.value
                        const n = parseMoneyInput(raw)
                        f.set(n === null ? '' : formatMoney(n))
                      }}
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <div className="text-[11px] font-semibold text-slate-500">입력합계</div>
                  <div className="mt-1 w-full rounded-md border bg-slate-50 px-3 py-2 text-sm flex items-center justify-between gap-2">
                    <div className="text-slate-700 font-semibold">합계</div>
                    <div className="text-slate-900 font-semibold">
                      {(() => {
                        const vals = [
                          parseMoneyInput(amtBuilding),
                          parseMoneyInput(amtHousehold),
                          parseMoneyInput(amtContents),
                          parseMoneyInput(amtFacility),
                          parseMoneyInput(amtMovables),
                          parseMoneyInput(amtMachinery),
                        ].filter((v) => typeof v === 'number')
                        const sum = vals.reduce((s, v) => s + v, 0)
                        return sum > 0 ? `${formatMoney(sum)} 만원` : '-'
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                * 숫자만 입력됩니다. (만원 단위 / 자동 콤마)
              </div>
            </div>
            </>
            ) : null}
            {result?.ok && currentStep === 3 && (
              <div className="md:col-span-2">
                <div className="text-xs font-semibold text-slate-500">동/호 선택(전유부)</div>
                <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                  {unitOptions.length > 0 ? (
                    <>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-500">동</div>
                        <select
                          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                          value={selectedDongNm}
                          onChange={(e) => {
                            const v = e.target.value
                            setSelectedDongNm(v)
                            setSelectedHoNm('')
                          }}
                        >
                          <option value="">동 선택</option>
                          {dongOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </div>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-500">호</div>
                        <select
                          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                          value={selectedHoNm}
                          onChange={(e) => setSelectedHoNm(e.target.value)}
                          disabled={!selectedDongNm}
                        >
                          <option value="">{selectedDongNm ? '호 선택' : '동을 먼저 선택'}</option>
                          {hoOptions.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-500">동(수동입력)</div>
                        <input
                          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                          placeholder="예) 101동"
                          value={selectedDongNm}
                          onChange={(e) => {
                            setSelectedDongNm(e.target.value)
                            // 호 입력은 유지
                          }}
                        />
                      </div>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-500">호(수동입력)</div>
                        <input
                          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                          placeholder="예) 1203호"
                          value={selectedHoNm}
                          onChange={(e) => setSelectedHoNm(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                  <div className="flex items-end">
                    <button
                      type="button"
                      className={[
                        'w-full rounded-md border px-3 py-2 text-sm disabled:opacity-50',
                        (!selectedDongNm || !selectedHoNm || loading) ? 'hover:bg-slate-50' : 'bg-brand-navy text-white border-brand-navy hover:opacity-90',
                      ].join(' ')}
                      disabled={!selectedDongNm || !selectedHoNm || loading}
                      onClick={onLookup}
                    >
                      동/호 적용
                    </button>
                  </div>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {unitOptions.length > 0
                    ? `* 전유부 목록 ${unitOptions.length}건. 동/호를 선택하면 전유공용면적(전유부) 정보를 추가 조회합니다.`
                    : '* 전유부 목록이 없어서 수동 입력으로만 적용됩니다. (일부 주소/대장 유형은 전유부 목록이 비어 있을 수 있어요)'}
                </div>
              </div>
            )}
            {result?.ok && currentStep === 3 && (
              <div className="md:col-span-2">
                <div className="text-xs font-semibold text-slate-500">층 선택(설계 메모 포함)</div>
                {floorRows.length === 0 ? (
                  <div className="mt-2 rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    층별개요 정보가 없습니다.
                  </div>
                ) : (
                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                    <div className="md:col-span-1">
                      <div className="flex items-center gap-3 text-sm text-slate-700">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="floorMode"
                            checked={floorSelectMode === 'ALL'}
                            onChange={() => {
                              setFloorSelectMode('ALL')
                              setSelectedFloorNoNm('')
                            }}
                          />
                          <span>전체층</span>
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="floorMode"
                            checked={floorSelectMode === 'ONE'}
                            onChange={() => setFloorSelectMode('ONE')}
                          />
                          <span>특정층</span>
                        </label>
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        * 설계의뢰 메모에 층별개요가 포함됩니다.
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[11px] font-semibold text-slate-500">선택 층</div>
                      <select
                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm disabled:bg-slate-50"
                        value={selectedFloorNoNm}
                        onChange={(e) => setSelectedFloorNoNm(e.target.value)}
                        disabled={floorSelectMode !== 'ONE'}
                      >
                        <option value="">{floorSelectMode === 'ONE' ? '층 선택' : '전체층 선택됨'}</option>
                        {floorOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
            {result?.ok && currentStep === 4 && (
              <div className="md:col-span-2">
                <div className="text-xs font-semibold text-slate-500">가입 유형(업종) / 특약</div>
                <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded-md border bg-white p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">가입 유형(업종) 선택</div>
                      {recommendedBizTypeIds.length > 0 && (
                        <div className="text-[11px] text-slate-500">
                          추천: <span className="font-semibold text-slate-700">{FIRE_BIZ_TYPES.find(t => t.id === recommendedBizTypeIds[0])?.label || '-'}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4">
                      {FIRE_BIZ_TYPES.map((t) => {
                        const active = t.id === selectedBizTypeId
                        const isRec = recommendedBizTypeIds.includes(t.id)
                        return (
                          <button
                            key={t.id}
                            type="button"
                            className={[
                              'rounded-md border px-3 py-2 text-sm text-left hover:bg-slate-50',
                              active ? 'border-brand-navy bg-slate-50' : 'bg-white',
                            ].join(' ')}
                            onClick={() => {
                              setSelectedBizTypeId(t.id)
                              setCheckedEndorsements(mergeEndorsements(t.endorsements || [], fireOccurrenceRecommendedEndorsements))
                            }}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-slate-900">{t.label}</span>
                              {isRec ? (
                                <span className="inline-flex items-center rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-800">
                                  추천
                                </span>
                              ) : null}
                            </div>
                            <div className="mt-0.5 text-[11px] text-slate-500">
                              필수 {t.endorsements.length}개
                            </div>
                          </button>
                        )
                      })}
                    </div>
                    <div className="mt-2 text-[11px] text-slate-500">
                      * 건축물대장만으로 업종을 100% 확정하기 어려워 추천은 참고용입니다. (미용실/학원 등은 직접 선택)
                    </div>
                  </div>

                  <div className="rounded-md border bg-white p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">특약 체크</div>
                      <div className="text-[11px] text-slate-500">
                        선택 {Array.isArray(checkedEndorsements) ? checkedEndorsements.length : 0}개
                      </div>
                    </div>
                    {selectedBizType ? (
                      <div className="mt-1 text-[11px] text-slate-500">
                        * <span className="font-semibold">{selectedBizType.label}</span> 필수 특약이 기본 체크됩니다. (직접 변경 가능)
                      </div>
                    ) : (
                      <div className="mt-1 text-[11px] text-slate-500">* 가입 유형(업종)을 먼저 선택하세요.</div>
                    )}
                    <div className="mt-3 max-h-[220px] overflow-auto rounded border bg-slate-50 p-2">
                      {FIRE_ENDORSEMENTS_ALL.map((name) => {
                        const checked = Array.isArray(checkedEndorsements) && checkedEndorsements.includes(name)
                        const isRequired = requiredEndorsements.includes(name)
                        const isOpenDataRecommended = fireOccurrenceRecommendedEndorsements.includes(name)
                        return (
                          <label key={name} className="flex items-start gap-2 px-2 py-1 text-sm text-slate-800">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={checked}
                              onChange={(e) => {
                                const on = e.target.checked
                                setCheckedEndorsements((prev) => {
                                  const cur = Array.isArray(prev) ? prev : []
                                  if (on) return Array.from(new Set([...cur, name]))
                                  return cur.filter((x) => x !== name)
                                })
                              }}
                            />
                            <span className="flex-1 break-words">
                              {name}
                              {isRequired ? (
                                <span className="ml-2 inline-flex items-center rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-700">
                                  필수
                                </span>
                              ) : null}
                              {isOpenDataRecommended ? (
                                <span className="ml-2 inline-flex items-center rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[10px] text-orange-800">
                                  공공데이터 추천
                                </span>
                              ) : null}
                            </span>
                          </label>
                        )
                      })}
                    </div>
                    <div className="mt-2 text-[11px] text-slate-500">
                      * 설계의뢰 메모에 체크된 특약이 자동 포함됩니다.
                    </div>
                  </div>
                </div>

                <div className="mt-3 rounded-md border border-orange-200 bg-orange-50 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-orange-950">지역 화재 통계 현황</div>
                      <div className="mt-1 text-[11px] text-orange-800">
                        {fireRegion.sido ? `${fireRegion.sido}${fireRegion.sigungu ? ` ${fireRegion.sigungu}` : ''}` : '선택 주소'}
                        {' '}기준
                      </div>
                    </div>
                  </div>

                  {fireOccurrenceLoading ? (
                    <div className="mt-3 text-xs text-orange-800">지역 화재 통계를 확인하는 중입니다.</div>
                  ) : null}
                  {fireOccurrenceError ? (
                    <div className="mt-3 text-xs text-orange-800">{fireOccurrenceError}</div>
                  ) : null}
                  {!fireOccurrenceLoading && !fireOccurrenceError && fireOccurrenceStats?.status !== 'READY' ? (
                    <div className="mt-3 text-xs text-orange-800">통계 데이터 없음</div>
                  ) : null}

                  {fireOccurrenceStats?.status === 'READY' ? (
                    <div className="mt-3 grid gap-3 md:grid-cols-3">
                      <div className="rounded border border-orange-200 bg-white/70 px-3 py-2">
                        <div className="text-[11px] font-semibold text-orange-800">화재 건수</div>
                        <div className="mt-1 text-base font-bold text-orange-950">
                          {formatNumber(fireOccurrenceStats?.overview?.fireCount) || '-'}
                        </div>
                      </div>
                      <div className="rounded border border-orange-200 bg-white/70 px-3 py-2 md:col-span-2">
                        <div className="text-[11px] font-semibold text-orange-800">주요 원인/장소</div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {[
                            ...(Array.isArray(fireOccurrenceStats?.topCauses) ? fireOccurrenceStats.topCauses : []).slice(0, 2).map((row) => statLabel(row, ['causeLarge', 'causeMiddle', 'causeSmall'])),
                            ...(Array.isArray(fireOccurrenceStats?.topPlaces) ? fireOccurrenceStats.topPlaces : []).slice(0, 2).map((row) => statLabel(row, ['placeLarge', 'placeMiddle', 'placeSmall'])),
                          ].filter(Boolean).map((label) => (
                            <span key={label} className="rounded border border-orange-200 bg-white px-2 py-1 text-[11px] text-orange-900">
                              {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
            <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-2 pt-2">
              <div className="text-[11px] text-slate-500">
                {currentStep === 1 ? '* 주소 검색 후 다음 단계로 진행하세요.' : null}
                {currentStep === 2 ? '* 건축물대장 조회 성공 후 다음 단계로 진행하세요.' : null}
                {currentStep === 3 ? '* 동/호·층 선택은 선택사항이며 건너뛸 수 있습니다.' : null}
                {currentStep === 4 ? '* 특약 체크 후 설계의뢰를 생성하세요.' : null}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="sgezOutline"
                  size="sm"
                  disabled={currentStep <= 1}
                  onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
                >
                  이전
                </Button>
                {currentStep === 1 ? (
                  <Button
                    type="button"
                    variant={hasAddrSelected ? 'sgez' : 'sgezOutline'}
                    size="sm"
                    disabled={!hasAddrSelected}
                    onClick={() => setCurrentStep(2)}
                  >
                    다음
                  </Button>
                ) : null}
                {currentStep === 2 ? (
                  <Button
                    type="button"
                    variant={lookupOk ? 'sgez' : 'sgezOutline'}
                    size="sm"
                    disabled={!lookupOk}
                    onClick={() => setCurrentStep(3)}
                  >
                    다음(동/호·층)
                  </Button>
                ) : null}
                {currentStep === 2 ? (
                  <Button
                    type="button"
                    variant={lookupOk ? 'sgez' : 'sgezOutline'}
                    size="sm"
                    disabled={!lookupOk}
                    onClick={() => setCurrentStep(4)}
                  >
                    다음(설계의뢰)
                  </Button>
                ) : null}
                {currentStep === 3 ? (
                  <Button
                    type="button"
                    variant={lookupOk ? 'sgez' : 'sgezOutline'}
                    size="sm"
                    disabled={!lookupOk}
                    onClick={() => setCurrentStep(4)}
                  >
                    다음
                  </Button>
                ) : null}
                {currentStep === 4 ? (
                  <Button
                    type="button"
                    variant={lookupOk ? 'sgez' : 'sgezOutline'}
                    size="sm"
                    disabled={!lookupOk}
                    onClick={() => setDesignOpen(true)}
                  >
                    설계의뢰
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="md:col-span-2">
              {/* <div className="text-xs font-semibold text-slate-500">법정동코드(bcode)</div>
              <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-800">
                {bcode || '-'}
              </div> */}
              {/* {!bcode && (
                <div className="mt-1 text-xs text-slate-500">
                  bcode가 없으면 서버가 JUSO로 정규화해야 합니다(현재는 JUSO 미사용 전제이므로 실패할 수 있어요).
                </div>
              )} */}
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
            </div>
          </div>

          {result && currentStep >= 2 && (
            <div className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="sgezOutline"
              size="sm"
              onClick={() => setDetailOpen(true)}
            >
              상세 보기
            </Button>
            {result?.ok && (
              <Button
                type="button"
                variant="sgez"
                size="sm"
                onClick={() => {
                  setDesignOpen(true)
                }}
              >
                설계의뢰
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">건물명</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{summary?.bldNm || '-'}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">주용도</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{summary?.purps || '-'}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">구조</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{summary?.strct || '-'}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">사용승인(준공)</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{summary?.useApr || '-'}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">연면적</div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {summary?.totArea ? `${formatNumber(summary.totArea)} ㎡` : '-'}
              </div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-xs font-semibold text-slate-500">층수</div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {(() => {
                  const g = summary?.grnd ? `${summary.grnd}층` : null
                  const u = summary?.ugrnd ? `지하 ${summary.ugrnd}층` : null
                  return [u, g].filter(Boolean).join(' / ') || '-'
                })()}
              </div>
            </div>
            <div className="rounded-xl border bg-white p-4 md:col-span-2">
              <div className="text-xs font-semibold text-slate-500">주택가격(건축물대장, 참고)</div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {housingPriceRows?.[0]?.hsprc ? `${formatNumber(housingPriceRows[0].hsprc)} 원` : '-'}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                기준일: {formatDateDigits(housingPriceRows?.[0]?.stdDay) || '-'}
              </div>
            </div>
          </div>

          <Modal
            open={detailOpen}
            title="건축물대장 상세"
            onClose={() => setDetailOpen(false)}
            headerRight={
              result?.ok ? (
                <button
                  type="button"
                  className="rounded bg-brand-navy text-white px-3 py-1.5 text-sm hover:opacity-90"
                  onClick={() => {
                    setDetailOpen(false)
                    setDesignOpen(true)
                  }}
                >
                  설계의뢰
                </button>
              ) : null
            }
          >
            <div className="grid grid-cols-1 gap-4">
              <BuildingCompactTable title="총괄개요" obj={result?.basisItem} />
              <AmountTable amounts={buildPrefill().fireAmounts} />
              <UnitAreaTable
                dongNm={result?.selectedDongNm}
                hoNm={result?.selectedHoNm}
                rows={result?.unitAreaRows}
              />
              <BuildingCompactTable title="표제부" obj={result?.titleItem} />
              <FloorTable
                rows={floorRows}
                selectedMode={floorSelectMode}
                selectedFloorNoNm={selectedFloorNoNm}
                onSelectMode={setFloorSelectMode}
                onSelectFloor={setSelectedFloorNoNm}
              />
              <HousingPriceTable rows={housingPriceRows} />
              <AtchJibunTable rows={atchJibunRows} />
              <JijiguTable rows={jijiguRows} />
            </div>
          </Modal>

          <Modal open={designOpen} title="화재보험 설계의뢰" onClose={() => setDesignOpen(false)}>
            <div className="space-y-4">
              <div className="rounded border bg-slate-50 p-3 text-sm">
                <div className="font-semibold text-slate-900">대상 주소</div>
                <div className="mt-1 text-slate-700">
                  {roadAddress || jibunAddress || '-'}
                </div>
              </div>

              <div className="rounded border bg-slate-50 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-xs font-semibold text-slate-500">고객명</div>
                    <div className="mt-1 font-medium text-slate-900">{String(customerName || '').trim() || '-'}</div>
                  </div>
                  <div className="text-xs text-slate-600">
                    가입설계동의여부: <span className="font-semibold">{commonExistingCustomer ? '예' : '아니오'}</span>
                  </div>
                </div>
              </div>

              <div className="rounded border p-3">
                <div className="text-sm font-semibold text-slate-900">요약(설계의뢰 메모로 전달)</div>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-slate-800 bg-slate-50 rounded p-3 overflow-auto">
                  {buildPrefill().commonNote}
                </pre>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                  onClick={() => setDesignOpen(false)}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="rounded bg-brand-navy text-white px-3 py-2 text-sm hover:opacity-90 disabled:opacity-50"
                  // disabled={!String(customerName || '').trim()}
                  onClick={() => {
                    const prefill = buildPrefill()
                    const key = `prefill.fire.${Date.now()}.${Math.random().toString(16).slice(2)}`
                    try { sessionStorage.setItem(key, JSON.stringify(prefill)) } catch { }
                    setDesignOpen(false)
                    navigate(`/requests/new?prefillKey=${encodeURIComponent(key)}`)
                  }}
                >
                  설계의뢰 생성하기
                </button>
              </div>
            </div>
          </Modal>
            </div>
          )}
        </>
      ) : (
        <div className="mt-6 rounded-xl border bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="w-full md:w-80 rounded-md border px-3 py-2 text-sm"
              placeholder="주소/고객명/건물명/용도 검색"
              value={historyQ}
              onChange={(e) => setHistoryQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') fetchHistoryList(historyQ)
              }}
            />
            <button
              type="button"
              className="rounded-md border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
              disabled={historyLoading}
              onClick={() => fetchHistoryList(historyQ)}
            >
              {historyLoading ? '조회 중...' : '검색'}
            </button>
            <button
              type="button"
              className="rounded-md border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
              disabled={historyLoading}
              onClick={() => {
                setHistoryQ('')
                fetchHistoryList('')
              }}
            >
              초기화
            </button>
          </div>

          {historyError && (
            <ErrorState
              description={historyError}
              onAction={() => fetchHistoryList(historyQ)}
              className="mt-3 rounded-xl border border-red-100 bg-white py-6"
            />
          )}

          {historyRows.length === 0 ? (
            historyLoading ? (
              <div className="mt-4 text-sm text-slate-500">이력을 불러오는 중입니다...</div>
            ) : (
              <EmptyState
                title="조회 이력이 없습니다"
                description="건축물대장 조회에 성공하면 이력이 자동 저장되고, 이후 같은 고객의 화재보험 설계를 빠르게 이어갈 수 있습니다."
                className="mt-4 rounded-xl border border-dashed bg-white"
              />
            )
          ) : (
            <>
              {/* 모바일 카드 형태 */}
              <div className="mt-4 space-y-3 xl:hidden">
                {historyRows.map((r) => (
                  <div key={r.id} className="rounded-lg border bg-white p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-500 mb-1">
                          {formatMenuInstant(r, 'createdAt')}
                        </div>
                        <div className="font-medium text-slate-900 mb-1">{r?.customerName || '-'}</div>
                        <div className="text-sm text-slate-700 break-words">
                          {r?.roadAddress || r?.jibunAddress || '-'}
                        </div>
                        {r?.roadAddress && r?.jibunAddress && r?.roadAddress !== r?.jibunAddress ? (
                          <div className="text-xs text-slate-500 break-words mt-1">{r.jibunAddress}</div>
                        ) : null}
                      </div>
                      {r?.requestId && (
                        <span className="inline-flex items-center rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-800 whitespace-nowrap">
                          설계의뢰 생성됨
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-xs text-slate-500">건물명</div>
                        <div className="font-medium text-slate-900">{r?.bldNm || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">용도</div>
                        <div className="text-slate-700">{r?.purps || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">동/호</div>
                        <div className="text-slate-700">
                          {r?.selectedDongNm || r?.selectedHoNm ? `${r?.selectedDongNm || '-'} / ${r?.selectedHoNm || '-'}` : '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">선택층</div>
                        <div className="text-slate-700">{r?.selectedFloorNoNm || '-'}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-xs text-slate-500">유형</div>
                        <div className="text-slate-700">{r?.bizTypeLabel || '-'}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                      {r?.requestId ? (
                        <button
                          type="button"
                          className="flex-1 rounded border px-3 py-2 text-xs hover:bg-slate-50"
                          onClick={() => navigate(`/requests/${r.requestId}`)}
                        >
                          요청 보기
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="flex-1 rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50"
                        disabled={historyLoading}
                        onClick={() => loadHistoryAndRestore(r.id)}
                      >
                        불러오기
                      </button>
                      <button
                        type="button"
                        className="flex-1 rounded bg-brand-navy text-white px-3 py-2 text-xs hover:opacity-90 disabled:opacity-50"
                        disabled={historyLoading}
                        onClick={() => loadHistoryAndRestore(r.id, { openDesign: true })}
                      >
                        설계의뢰
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {/* PC 테이블 형태 */}
              <div className="mt-4 hidden overflow-x-auto xl:block">
                <table className="w-full text-sm table-fixed min-w-[1200px]">
                  <colgroup>
                    <col className="w-[70px]" />
                    <col className="w-[70px]" />
                    <col className="w-[200px]" />
                    <col className="w-[130px]" />
                    <col className="w-[100px]" />
                    <col className="w-[60px]" />
                    <col className="w-[100px]" />
                    <col className="w-[100px]" />
                  </colgroup>
                  <thead className="border-b border-sgez-border-strong bg-sgez-surface-sunken text-slate-700">
                    <tr>
                      <th className="text-left px-3 py-2 whitespace-nowrap">일시</th>
                      <th className="text-left px-3 py-2 whitespace-nowrap">고객명</th>
                      <th className="text-left px-3 py-2">주소</th>
                      <th className="text-left px-3 py-2">건물/용도</th>
                      <th className="text-left px-3 py-2 whitespace-nowrap">동/호</th>
                      <th className="text-left px-3 py-2 whitespace-nowrap">선택층</th>
                      <th className="text-left px-3 py-2">유형</th>
                      <th className="text-right px-3 py-2 whitespace-nowrap"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {historyRows.map((r) => (
                      <tr key={r.id} className="text-slate-900">
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                          {formatMenuInstant(r, 'createdAt')}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{r?.customerName || '-'}</td>
                        <td className="px-3 py-2">
                          <div className="text-slate-900 break-words">{r?.roadAddress || r?.jibunAddress || '-'}</div>
                          {r?.roadAddress && r?.jibunAddress && r?.roadAddress !== r?.jibunAddress ? (
                            <div className="text-[11px] text-slate-500 break-words">{r.jibunAddress}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium break-words">{r?.bldNm || '-'}</div>
                          <div className="text-[11px] text-slate-500 break-words">{r?.purps || '-'}</div>
                          {r?.requestId ? (
                            <div className="mt-1">
                              <span className="inline-flex items-center rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-800">
                                설계의뢰 생성됨
                              </span>
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r?.selectedDongNm || r?.selectedHoNm ? `${r?.selectedDongNm || '-'} / ${r?.selectedHoNm || '-'}` : '-'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{r?.selectedFloorNoNm || '-'}</td>
                        <td className="px-3 py-2 break-words">{r?.bizTypeLabel || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">
                          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                            {r?.requestId ? (
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                                onClick={() => navigate(`/requests/${r.requestId}`)}
                              >
                                요청 보기
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="rounded border px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                              disabled={historyLoading}
                              onClick={() => loadHistoryAndRestore(r.id)}
                            >
                              재설계
                            </button>
                            <button
                              type="button"
                              className="rounded bg-brand-navy text-white px-2 py-1 text-xs hover:opacity-90 disabled:opacity-50"
                              disabled={historyLoading}
                              onClick={() => loadHistoryAndRestore(r.id, { openDesign: true })}
                            >
                              설계의뢰
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

